import PropTypes from 'prop-types'
import { reflectPromise } from './promises'

/**
 * This is a generic promise pool executor, which runs the promises in the provided pool
 * sequentially and in parallel among the number of connections initiated (default to 2
 * works).
 * After the pool is emptied, the returned promise can be resolved to the _result of all
 * promises from the pool as to an array.
 *
 * @class PromisePool
 * @classdesc This is a generic promise pool executor
 *
 * @param {Array.<Promise<any[]>>} pool   - The pool of promises waiting to be executed.
 * @param {Number} connections                   - The connections can be established to execute
 *                                                 the actions in the pool simultaneously.
 * @param {Function} [onStepCompleted]           - Callback to called after every completed promise.
 * @param {boolean} [reflect]                    - whether to reflect the promises in the pool (ignore errors)
 */
class PromisePool {
    constructor({ connections = 2, onStepCompleted = Function.prototype, pool = [], reflect = false }) {
        this.pool = pool
        this.connections = connections
        this._addStartedPromise = this._addStartedPromise.bind(this)
        this.onStepCompleted = onStepCompleted
        this._shouldReflect = reflect
    }

    start() {
        this._result = []
        this._data = []
        this._completed = 0
        this._failed = 0
        this._shouldCancel = false
        this._started = Date.now()

        let myPool = [...this.pool]

        if (this._shouldReflect) {
            myPool = myPool.map(call => () => reflectPromise(call()))
        }

        this._handleCallback()
        const queue = myPool.splice(0, this.connections).map(call => {
            const prom = call()
            this._addStartedPromise(prom)
            return prom.then(result => this._handleCallback(result)).then(() => this._startNext(myPool))
        })
        return Promise.all(queue).then(() => {
            return Promise.all(this._result)
        })
    }

    cancel() {
        this._shouldCancel = true
    }

    _addStartedPromise(prom) {
        this._result.push(prom)
    }

    _handleCallback(result) {
        if (result && this._shouldReflect && result.isRejected) {
            this._failed++
        } else if (result) {
            this._completed++
            if (this._shouldReflect) {
                this._data.push(result.result)
            } else {
                this._data.push(result)
            }
        }

        this.onStepCompleted({
            completed: this._completed,
            data: this._data,
            duration: Date.now() - this._started,
            failed: this._failed,
            total: this.pool.length
        })
    }

    _startNext(pool) {
        if (this._shouldCancel) {
            return Promise.reject({ canceled: true })
        }

        if (pool.length) {
            const prom = pool.shift()()
            this._addStartedPromise(prom)
            return prom.then(result => this._handleCallback(result)).then(() => this._startNext(pool))
        }
        return Promise.resolve()
    }
}

const promisePoolProgressPropType = PropTypes.shape({
    completed: PropTypes.number,
    duration: PropTypes.number,
    failed: PropTypes.number,
    total: PropTypes.number
})

export { promisePoolProgressPropType, PromisePool }
